/**
 * Enterprise-grade Logging Service
 * Provides centralized logging with different log levels
 * and integration with external monitoring services
 */

const LOG_LEVELS = {
    DEBUG: "DEBUG",
    INFO: "INFO",
    WARN: "WARN",
    ERROR: "ERROR",
    FATAL: "FATAL",
};

class LoggingService {
    constructor() {
        this.isProduction = process.env.NODE_ENV === "production";
        this.logs = [];
        this.maxLogsInMemory = 100;
        // Store original console methods to avoid circular dependencies
        // when console.error/warn are patched to call this service
        this.originalConsoleError = console.error.bind(console);
        this.originalConsoleWarn = console.warn.bind(console);
        this.originalConsoleInfo = console.info.bind(console);
        this.originalConsoleDebug = console.debug.bind(console);
    }

    /**
     * Format log entry with metadata
     */
    static formatLog(level, message, metadata = {}) {
        return {
            timestamp: new Date().toISOString(),
            level,
            message,
            metadata: {
                ...metadata,
                userAgent: navigator.userAgent,
                url: window.location.href,
                environment: process.env.NODE_ENV,
            },
        };
    }

    /**
     * Store log in memory (circular buffer)
     */
    storeLog(logEntry) {
        this.logs.push(logEntry);
        if (this.logs.length > this.maxLogsInMemory) {
            this.logs.shift();
        }
    }

    /**
     * Send logs to external monitoring service
     * Replace with your actual monitoring service (Sentry, LogRocket, etc.)
     */
    async sendToMonitoring(logEntry) {
        if (!this.isProduction) {
            return;
        }

        try {
            // Example: Send to your monitoring service
            // await axios.post('/api/logs', logEntry);

            // For now, just store it
            this.originalConsoleInfo("Log would be sent to monitoring service:", logEntry);
        } catch (error) {
            // Silently fail - don't want logging errors to break the app
            // Use original console.error to avoid circular dependency
            this.originalConsoleError("Failed to send log to monitoring service:", error);
        }
    }

    /**
     * Debug level logging
     */
    debug(message, metadata) {
        const logEntry = LoggingService.formatLog(LOG_LEVELS.DEBUG, message, metadata);
        this.storeLog(logEntry);

        if (!this.isProduction) {
            this.originalConsoleDebug(`[${LOG_LEVELS.DEBUG}]`, message, metadata);
        }
    }

    /**
     * Info level logging
     */
    info(message, metadata) {
        const logEntry = LoggingService.formatLog(LOG_LEVELS.INFO, message, metadata);
        this.storeLog(logEntry);

        if (!this.isProduction) {
            this.originalConsoleInfo(`[${LOG_LEVELS.INFO}]`, message, metadata);
        }
    }

    /**
     * Warning level logging
     */
    warn(message, metadata) {
        const logEntry = LoggingService.formatLog(LOG_LEVELS.WARN, message, metadata);
        this.storeLog(logEntry);
        this.sendToMonitoring(logEntry);

        // Use original console.warn to avoid circular dependency
        this.originalConsoleWarn(`[${LOG_LEVELS.WARN}]`, message, metadata);
    }

    /**
     * Error level logging
     */
    error(message, metadata) {
        const logEntry = LoggingService.formatLog(LOG_LEVELS.ERROR, message, metadata);
        this.storeLog(logEntry);
        this.sendToMonitoring(logEntry);

        // Use original console.error to avoid circular dependency
        this.originalConsoleError(`[${LOG_LEVELS.ERROR}]`, message, metadata);
    }

    /**
     * Fatal level logging
     */
    fatal(message, metadata) {
        const logEntry = LoggingService.formatLog(LOG_LEVELS.FATAL, message, metadata);
        this.storeLog(logEntry);
        this.sendToMonitoring(logEntry);

        // Use original console.error to avoid circular dependency
        this.originalConsoleError(`[${LOG_LEVELS.FATAL}]`, message, metadata);
    }

    /**
     * Log error with full stack trace
     */
    logError(error, additionalData = {}) {
        const errorData = {
            message: error.message,
            stack: error.stack,
            name: error.name,
            ...additionalData,
        };

        this.error("Application Error", errorData);
    }

    /**
     * Get all logs from memory
     */
    getLogs() {
        return [...this.logs];
    }

    /**
     * Clear all logs from memory
     */
    clearLogs() {
        this.logs = [];
    }

    /**
     * Export logs as JSON
     */
    exportLogs() {
        return JSON.stringify(this.logs, null, 2);
    }
}

// Create singleton instance
const loggingService = new LoggingService();

// Export convenience functions
export const debug = (message, metadata) => loggingService.debug(message, metadata);
export const info = (message, metadata) => loggingService.info(message, metadata);
export const warn = (message, metadata) => loggingService.warn(message, metadata);
export const error = (message, metadata) => loggingService.error(message, metadata);
export const fatal = (message, metadata) => loggingService.fatal(message, metadata);
export const logError = (err, additionalData) => loggingService.logError(err, additionalData);
export const getLogs = () => loggingService.getLogs();
export const clearLogs = () => loggingService.clearLogs();
export const exportLogs = () => loggingService.exportLogs();

export default loggingService;
